import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Icon } from '../../design/Icon';
import { fmt } from '../../design/format';
import { Page, useAccountTimeZone, useTerms } from '../../layout';
import { useAuth } from '../../contexts/AuthContext';
import { LanguagePill } from '../../components/shared/Badges';
import { Alert } from '../../components/shared/Feedback';
import { Dialog, useToast } from '../../components/shared/Overlay';
import { ErrorState, Skeleton } from '../../components/shared/States';
import { RecordingPlayer } from '../../components/shared/RecordingPlayer';
import { callsApi } from '../../services/callsApi';
import { asDate, fmtClock, fmtLongDay, fmtTime, shortCallId } from './callFormat';
import { languageCode } from './callFilters';
import { CallRoute } from './detail/CallRoute';
import { TranscriptCard } from './detail/TranscriptCard';
import { AiPanel } from './detail/AiPanel';
import { SetCommunityDrawer } from './detail/SetCommunityDrawer';
import { callerDirectoryLine, entryTypeLabel } from '../admin/directoryModel';
import {
  buildRoute,
  buildSpeakers,
  buildTurns,
  callerCaption,
  displaySentiment,
  languageName,
  locateEvidence,
  methodLabel,
  recordingOffsetSeconds,
  redactionPhrase,
  seedOf,
  sentimentCaption,
  transcriptionLabel,
  waveBars,
} from './detail/callDetailModel';
import './detail/callDetail.css';

const MANAGE_SETTINGS = 'account:manage_settings';
const MANAGE_DIRECTORY = 'directory:manage';
const SET_COMMUNITY_HASH = '#set-community';
const FLASH_MS = 1600;

const optional = (promise) => promise.then((data) => ({ data })).catch((error) => ({ error }));

/** Transcript segment an evidence link points at: the segment holding its mark, else the segment playing at the quote's time. */
function segmentOf(marks, markId, turns, quote) {
  const marked = Object.keys(marks).find((index) => marks[index].some((m) => m.id === markId));
  if (marked != null) return Number(marked);
  const at = (turns || []).filter((t) => t.kind === 'segment' && t.t <= quote.startMs / 1000).pop();
  return at ? at.index : null;
}

function attributionPhrase(source) {
  return { AnalysisExtraction: "from caller's words", CallQueueMapping: 'from the queue', AutoAttendantMapping: 'from the auto attendant', DirectoryMatch: 'from the caller directory', Manual: 'set by hand' }[source] || '';
}

function outcomeText(call, route) {
  const transferred = route.some((h) => h.type === 'Transfer');
  const answered = route.some((h) => h.type === 'Agent');
  if (call.outcome === 'Abandoned') return 'Abandoned';
  if (call.outcome === 'Voicemail') return 'Voicemail';
  if (transferred && answered) return 'Answered after transfer';
  return call.outcome === 'Transferred' ? 'Transferred' : call.outcome || 'Unknown';
}

function CallerValue({ call, analysis }) {
  const listed = call.callerDirectory;
  const name = listed?.fullName || analysis?.caller?.name || call.callerName || call.callerNumber || 'Unknown caller';
  if (!listed?.isInternal) return name;
  return (
    <>
      {name} <span className="badge b-info">{entryTypeLabel(listed.entryType)}</span>
    </>
  );
}

function SummaryStrip({ call, route, analysis, transcript, onChangeCommunity }) {
  const terms = useTerms();
  const lastQueue = [...route].reverse().find((h) => h.type === 'Queue' || h.type === 'Transfer');
  const classifications = analysis?.classifications || [];
  const urgent = classifications.filter((c) => c.severity === 'Immediate').length;
  const soon = classifications.filter((c) => c.severity === 'Within48Hours').length;
  const start = displaySentiment(analysis?.sentiment?.callerStart);
  const end = displaySentiment(analysis?.sentiment?.callerEnd);
  const confidence = transcript?.languageConfidence != null ? `${Math.round(transcript.languageConfidence * 100)}%` : '';
  const method = transcript?.languageDetectionMethod;
  const languageSub = method === 'LanguageModelConfirmation' ? `Model confirmation${confidence ? `, speech engine ${confidence}` : ''}` : `${methodLabel(method)}${confidence ? `, ${confidence}` : ''}`;
  const items = [
    ['Outcome', outcomeText(call, route), lastQueue?.name || call.queueName || ''],
    [terms.community, call.communityName || 'Not identified', [call.hierarchyNodeName, attributionPhrase(call.communityAttributionSource)].filter(Boolean).join(' · ')],
    ['Caller', <CallerValue call={call} analysis={analysis} />, callerDirectoryLine(call.callerDirectory) || callerCaption(analysis)],
    ['Language', languageName(transcript?.language || call.language) || 'Not detected', transcript ? languageSub : ''],
    ['Caller sentiment', start && end ? (start === end ? end : `${start} → ${end}`) : end || '—', sentimentCaption(analysis)],
    ['Classifications', String(classifications.length), [urgent ? `${urgent} immediate` : '', soon ? `${soon} within 48 hours` : ''].filter(Boolean).join(', ') || (classifications.length ? 'none urgent' : '')],
  ];
  return (
    <section className="card">
      <div className="sumstrip" id="sum">
        {items.map(([label, value, sub]) => (
          <div key={label}>
            <div className="l">
              {label}
              {label === terms.community && onChangeCommunity && (
                <>
                  {' '}
                  <button className="btn btn-ghost btn-sm" type="button" id="set-comm" style={{ height: 20, padding: '0 6px', marginLeft: 2 }} onClick={onChangeCommunity}>
                    Change
                  </button>
                </>
              )}
            </div>
            <div className="v">{value}</div>
            <div className="s">{sub}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

const RECENT_CALL_MINUTES = 120;

const minutesSinceEnd = (call) => Math.max(0, Math.round((Date.now() - asDate(call.endTime || call.startTime).getTime()) / 60000));

/** A call whose transcript may still arrive: analysis pending, or ended recently (no summary exists until transcription finishes). */
const awaitingTranscript = (call) => call.analysisStatus === 'Pending' || (!call.analysisStatus && minutesSinceEnd(call) < RECENT_CALL_MINUTES);

function ContentNotice({ call, contentState }) {
  if (call.contentDeletedAt)
    return <p className="caption">An administrator deleted this call's transcript and analysis on {fmtLongDay(call.contentDeletedAt)}. The call record stays; recordings are never stored by Vertolytics.</p>;
  if (call.analysisStatus === 'NotApplicable') return <p className="caption">Not analyzed: no recording. Calls that reached no one, or that Teams did not record, have no transcript.</p>;
  if (contentState === 'forbidden') return <p className="caption">Your role doesn't include call transcripts and AI analysis.</p>;
  if (call.analysisStatus === 'Failed') return <p className="caption">Transcription or analysis failed for this call. An administrator can re-run it.</p>;
  if (!awaitingTranscript(call))
    return <p className="caption">No transcript for this call. Transcription may be turned off for your organization, or this call wasn't sent for transcription.</p>;
  const minutes = minutesSinceEnd(call);
  return (
    <Alert icon="clock" title="Transcribing and analyzing">
      This call ended {minutes < RECENT_CALL_MINUTES ? `${minutes} minutes` : 'a while'} ago. Transcripts and AI analysis usually appear 15 to 30 minutes after a call ends. The recording is ready to play now.
    </Alert>
  );
}

/** Call detail (mockup 13-call-detail.html): lineage, recording, diarized transcript with evidence, AI analysis. */
export function CallDetailPage() {
  const { id } = useParams();
  useAccountTimeZone();
  const { hasPermission } = useAuth();
  const toast = useToast();
  const playerRef = useRef(null);
  const [state, setState] = useState({ status: 'loading' });
  const [reload, setReload] = useState(0);
  const [position, setPosition] = useState(0);
  const [activeMark, setActiveMark] = useState(null);
  const [targetIndex, setTargetIndex] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [settingCommunity, setSettingCommunity] = useState(location.hash === SET_COMMUNITY_HASH);
  const flashTimer = useRef(null);
  const canManage = hasPermission(MANAGE_SETTINGS);
  const canSetCommunity = hasPermission(MANAGE_DIRECTORY);

  const closeSetCommunity = () => {
    setSettingCommunity(false);
    if (location.hash === SET_COMMUNITY_HASH) navigate({ hash: '' }, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      try {
        const [call, journey, classifications] = await Promise.all([callsApi.get(id), optional(callsApi.journey(id)), optional(callsApi.classifications())]);
        const content = call.hasTranscript && !call.contentDeletedAt ? await Promise.all([optional(callsApi.transcript(id)), optional(callsApi.analysis(id))]) : [{}, {}];
        if (cancelled) return;
        const forbidden = [content[0].error, content[1].error].some((e) => e?.response?.status === 403);
        setState({
          status: 'ready',
          call,
          journey: journey.data || null,
          transcript: content[0].data || null,
          analysis: content[1].data || null,
          taxonomy: classifications.data || null,
          contentState: forbidden ? 'forbidden' : 'ok',
        });
      } catch (error) {
        if (!cancelled) setState({ status: 'error', error });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, reload]);

  const model = useMemo(() => {
    if (state.status !== 'ready') return null;
    const { call, journey, transcript, analysis } = state;
    const route = buildRoute(journey);
    const recordingSeconds = transcript?.durationMs ? transcript.durationMs / 1000 : Math.max(0, (call.duration || 0) - recordingOffsetSeconds(analysis, route));
    const { speakers, byLabel } = buildSpeakers(transcript, analysis, call, route);
    const turns = transcript ? buildTurns(transcript, analysis, route, byLabel, recordingSeconds) : null;
    const quotes = analysis ? [...(analysis.classifications || []).flatMap((c) => c.evidence || []), ...(analysis.evidence || [])] : [];
    const { marks, quoteMark } = locateEvidence(transcript, quotes);
    const firstAgent = route.find((h) => h.type === 'Agent');
    return { route, recordingSeconds, speakers, byLabel, turns, marks, quoteMark, firstAgent, offset: recordingOffsetSeconds(analysis, route), bars: turns ? waveBars(turns, recordingSeconds, seedOf(call.id)) : [] };
  }, [state]);

  const flash = useCallback((markId) => {
    setActiveMark(markId);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setActiveMark(null), FLASH_MS);
  }, []);

  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const seek = useCallback((seconds) => {
    if (playerRef.current) playerRef.current.seek(seconds);
  }, []);

  const onMarkClick = (markId) => {
    const target = document.querySelector(`[data-evf="${markId}"]`);
    if (target) target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    flash(markId);
  };

  const onQuote = (quote, markId) => {
    setTargetIndex(segmentOf(model.marks, markId, model.turns, quote));
    if (markId) {
      const mark = document.querySelector(`mark.ev[data-ev="${markId}"]`);
      if (mark) mark.scrollIntoView({ block: 'center', behavior: 'smooth' });
      flash(markId);
    }
    seek(quote.startMs / 1000);
  };

  if (state.status === 'loading')
    return (
      <Page title="Call" crumbs={[{ label: 'Calls', to: '/calls' }, { label: `Call ${shortCallId(id)}` }]}>
        <div className="stack-lg">
          <Skeleton height={64} />
          <Skeleton height={220} />
          <Skeleton height={360} />
        </div>
      </Page>
    );

  if (state.status === 'error')
    return (
      <Page title="Call" crumbs={[{ label: 'Calls', to: '/calls' }, { label: `Call ${shortCallId(id)}` }]}>
        <ErrorState
          title="This call couldn't load"
          body={
            state.error?.response?.status === 404
              ? "Vertolytics couldn't find this call. It may be outside the calls you can see, deleted from Microsoft Teams, or older than the retention period."
              : "Vertolytics couldn't read this call record. Retry in a moment."
          }
          error={state.error}
          onRetry={() => setReload((n) => n + 1)}
        />
      </Page>
    );

  const { call, journey, transcript, analysis, taxonomy, contentState } = state;
  const caller = call.callerDirectory?.fullName || analysis?.caller?.name || call.callerName;
  const title = `${call.direction === 'Outbound' || call.direction === 'Internal' ? 'Call to' : 'Call from'} ${caller || call.callerNumber || 'unknown caller'}`;
  const language = languageName(transcript?.language || call.language);
  const subtitle = (
    <>
      {[call.callerNumber, `${fmtLongDay(call.startTime)}, ${fmtTime(call.startTime)}`, `${fmt.mmss(call.duration)} total`].filter(Boolean).join(' · ')}
      {language && (
        <>
          {' · '}
          <LanguagePill code={languageCode(transcript?.language || call.language)} title={language} />
        </>
      )}
    </>
  );
  const speakerName = (quote) => {
    if (quote.speakerRole === 'Caller') return 'Caller';
    return model.byLabel[quote.speakerLabel]?.name || quote.speakerLabel || 'Agent';
  };
  const currentTurn = model.turns ? model.turns.filter((t) => t.kind === 'segment' && t.t <= position).pop() : null;
  const recordingStart = asDate(call.startTime).getTime() + model.offset * 1000;
  const taxonomyName = analysis && taxonomy?.versionId === analysis.taxonomyVersionId ? taxonomy.versionName : null;
  const translated = Boolean(analysis && Object.keys(analysis.translatedSegments || {}).length);
  const speakerCount = model.speakers.length;
  const redaction = redactionPhrase(transcript);
  const synthetic = transcript?.provider === 'Synthetic';
  const transcriptSub = transcript
    ? [transcriptionLabel(transcript.provider), `${speakerCount} ${speakerCount === 1 ? 'speaker' : 'speakers'}`, translated ? `${language}, shown with English translation` : language, redaction ? `${redaction} redacted` : null].filter(Boolean).join(' · ')
    : call.contentDeletedAt
    ? 'Content deleted'
    : call.analysisStatus === 'NotApplicable'
    ? 'No recording'
    : awaitingTranscript(call)
    ? 'Not transcribed yet'
    : 'Not transcribed';

  const rerun = async () => {
    try {
      await callsApi.rerunAnalysis(call.id);
      toast('Analysis queued again; results replace this analysis in a few minutes');
    } catch (error) {
      toast(error.response?.data?.message || "The analysis couldn't be queued", 'alert');
    }
  };

  const deleteContent = async () => {
    setConfirmDelete(false);
    try {
      await callsApi.deleteContent(call.id);
      toast('Transcript and analysis deleted for this call');
      setReload((n) => n + 1);
    } catch (error) {
      toast(error.response?.data?.message || "The call's content couldn't be deleted", 'alert');
    }
  };

  const actions = (
    <div className="row mt-3" style={{ flexWrap: 'wrap' }}>
      {canManage && (
        <button className="btn btn-ghost btn-sm" type="button" onClick={rerun}>
          <Icon name="refresh" className="ico-sm" />
          Re-run analysis
        </button>
      )}
      {canManage && (
        <button className="btn btn-ghost btn-sm" type="button" onClick={() => setConfirmDelete(true)}>
          <Icon name="trash" className="ico-sm" />
          Delete call content
        </button>
      )}
    </div>
  );

  return (
    <Page
      title={title}
      subtitle={subtitle}
      documentTitle={title}
      crumbs={[{ label: 'Calls', to: '/calls' }, { label: `Call ${shortCallId(call.callId || call.id)}` }]}
      exportConfig={{ name: `Call ${shortCallId(call.callId || call.id)}`, endpoint: `/export/calls/${encodeURIComponent(call.id)}`, extraFormats: [{ format: 'pdf', label: 'Call summary PDF', description: 'Call route, AI analysis, and transcript (.pdf)' }] }}
    >
      <div className="stack-lg">
        <SummaryStrip call={call} route={model.route} analysis={analysis} transcript={transcript} onChangeCommunity={canSetCommunity ? () => setSettingCommunity(true) : null} />
        {model.route.length > 0 && <CallRoute route={model.route} startTime={journey?.startTime || call.startTime} stepsTruncated={journey?.stepsTruncated} />}
        <div className="grid">
          <div className="c-7 stack">
            <section className="card">
              <div className="card-head">
                <div>
                  <div className="t">Recording</div>
                  <div className="d" id="rec-sub">
                    {model.firstAgent ? `Starts when ${model.firstAgent.name} answered at ${fmtClock(new Date(recordingStart))} · ${fmt.mmss(model.recordingSeconds)}` : `${fmt.mmss(model.recordingSeconds)}`}
                  </div>
                </div>
                <div className="acts">
                  <span className="badge">
                    <Icon name="lock" className="ico-sm" />
                    Streamed from Teams, not stored
                  </span>
                </div>
              </div>
              <div className="card-body">
                {synthetic ? (
                  <p className="caption" data-testid="recording-state" data-state="unavailable">
                    No recording. This call's transcript is synthetic demo data, generated from the call's details; there is no audio to play.
                  </p>
                ) : (
                  <RecordingPlayer
                    ref={playerRef}
                    callId={call.id}
                    durationSeconds={model.recordingSeconds}
                    bars={model.bars}
                    onPosition={setPosition}
                    legend={
                      <div className="legend" id="spk">
                        {model.speakers.map((s) => (
                          <span key={s.key}>
                            <i style={{ background: s.color }} />
                            {s.isCaller ? `${s.name} (caller)` : s.name}
                          </span>
                        ))}
                      </div>
                    }
                  />
                )}
              </div>
            </section>
            <TranscriptCard
              subtitle={transcriptSub}
              turns={model.turns}
              marks={model.marks}
              currentIndex={currentTurn?.index}
              targetIndex={targetIndex}
              translated={translated}
              activeMark={activeMark}
              onSeek={seek}
              onMarkEnter={setActiveMark}
              onMarkLeave={() => setActiveMark(null)}
              onMarkClick={onMarkClick}
            >
              {!model.turns && <ContentNotice call={call} contentState={contentState} />}
            </TranscriptCard>
          </div>
          {analysis ? (
            <AiPanel
              call={call}
              analysis={analysis}
              transcript={transcript}
              taxonomyName={taxonomyName}
              quoteMark={model.quoteMark}
              speakerName={speakerName}
              activeMark={activeMark}
              onQuote={onQuote}
              onMarkEnter={setActiveMark}
              onMarkLeave={() => setActiveMark(null)}
              actions={actions}
            />
          ) : (
            <aside className="c-5">
              <section className="card ai" aria-label="AI analysis" data-testid="ai-panel">
                <div className="card-head" style={{ paddingBottom: 12, borderBottom: '1px solid var(--divider)' }}>
                  <div>
                    <div className="t row" style={{ gap: 8 }}>
                      <Icon name="sparkle" />
                      AI analysis
                    </div>
                  </div>
                  <div className="acts">
                    <span className="badge">{call.analysisStatus === 'Pending' ? 'Pending' : 'Not available'}</span>
                  </div>
                </div>
                <div style={{ padding: 16 }}>
                  <div className="caption">
                    {call.analysisStatus === 'Pending'
                      ? 'Summary, classifications, entities, sentiment, and action items appear here when analysis finishes.'
                      : call.contentDeletedAt
                      ? 'The analysis was deleted with the transcript.'
                      : call.analysisStatus === 'NotApplicable'
                      ? 'Not analyzed: no recording.'
                      : contentState === 'forbidden'
                      ? "Your role doesn't include AI analysis of calls."
                      : 'No analysis for this call.'}
                  </div>
                  {canManage && !call.contentDeletedAt && call.analysisStatus !== 'NotApplicable' && call.hasTranscript && actions}
                </div>
              </section>
            </aside>
          )}
        </div>
      </div>
      <SetCommunityDrawer
        open={canSetCommunity && settingCommunity}
        call={call}
        callerName={analysis?.caller?.name || call.callerName}
        callerUnit={analysis?.property?.unitOrLot}
        onClose={closeSetCommunity}
        onSaved={(updated, message) => {
          closeSetCommunity();
          setState((s) => ({ ...s, call: { ...s.call, ...updated } }));
          toast(message);
        }}
      />
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        title="Delete this call's content?"
        footer={
          <>
            <button className="btn" type="button" onClick={() => setConfirmDelete(false)}>
              Cancel
            </button>
            <button className="btn btn-danger" type="button" onClick={deleteContent}>
              Delete transcript and analysis
            </button>
          </>
        }
      >
        <p className="small">
          Use this when a caller objected to the recording and asked for the call to be deleted (Recording Objection). It deletes the transcript and the AI analysis of this call
          for good. Recordings are never stored by Vertolytics, so there is nothing else to delete; the call record from Microsoft Teams stays, and the call is not transcribed again.
        </p>
      </Dialog>
    </Page>
  );
}
